import {
  type ContextWindowSnapshot,
  formatContextWindowTokens,
  formatCostUsd,
} from "~/lib/contextWindow";
import { formatRateLimitResetTime, type VisibleRateLimitRow } from "~/lib/rateLimits";
import { Popover, PopoverPopup, PopoverTrigger } from "../ui/popover";

function formatPercentage(value: number | null): string | null {
  if (value === null || !Number.isFinite(value)) {
    return null;
  }
  if (value < 10) {
    return `${value.toFixed(1).replace(/\.0$/, "")}%`;
  }
  return `${Math.round(value)}%`;
}

/**
 * The provider account's plan quota, appended to the context-window popover.
 *
 * Two very different numbers share this popover on purpose. The context window
 * is about *this conversation* and resets when the thread does; the plan quota
 * is about the shared account and is what actually stops work. Someone deciding
 * whether to keep going needs both, and the composer is where that decision
 * gets made — a settings screen they would have to go look at is a screen they
 * check after being cut off, not before.
 *
 * Rendered only for holders of `limits.view`; the caller decides, so this
 * component stays presentation-only.
 */
function PlanLimits(props: {
  rows: ReadonlyArray<VisibleRateLimitRow>;
  subscriptionType?: string | null | undefined;
}) {
  const { rows, subscriptionType } = props;
  if (rows.length === 0) return null;

  return (
    <div className="space-y-1 border-t border-border/50 pt-2">
      <div className="text-[11px] font-medium uppercase tracking-[0.08em] text-muted-foreground">
        Plan limits{subscriptionType ? ` · ${subscriptionType}` : ""}
      </div>
      {rows.map((row) => (
        <div
          key={row.id}
          className="flex items-baseline justify-between gap-4 whitespace-nowrap text-xs"
        >
          <span className="text-muted-foreground">{row.label}</span>
          <span className="flex items-baseline gap-2 tabular-nums">
            <span className="font-medium text-foreground">{Math.round(row.usedPercent)}% used</span>
            {row.resetsAt ? (
              <span className="text-muted-foreground">
                resets {formatRateLimitResetTime(row.resetsAt)}
              </span>
            ) : null}
          </span>
        </div>
      ))}
    </div>
  );
}

export function ContextWindowMeter(props: {
  usage: ContextWindowSnapshot;
  cumulativeCostUsd?: number | null | undefined;
  /**
   * Plan-quota rows to append. Empty or omitted renders nothing — which is both
   * "this account has no plan limits" and "you may not see them", and the
   * popover must look identical in either case.
   */
  planLimits?: ReadonlyArray<VisibleRateLimitRow> | undefined;
  planSubscriptionType?: string | null | undefined;
}) {
  const { usage, cumulativeCostUsd, planLimits, planSubscriptionType } = props;
  const usedPercentage = formatPercentage(usage.usedPercentage);
  const normalizedPercentage = Math.max(0, Math.min(100, usage.usedPercentage ?? 0));
  const radius = 6;
  const circumference = 2 * Math.PI * radius;
  const dashOffset = circumference - (normalizedPercentage / 100) * circumference;

  return (
    <Popover>
      <PopoverTrigger
        openOnHover
        delay={150}
        closeDelay={0}
        render={
          <button
            type="button"
            className="group inline-flex items-center gap-1.5 rounded-full px-1 py-0.5 text-[10px] text-muted-foreground/60 transition-colors hover:text-muted-foreground"
            aria-label={
              usage.maxTokens !== null && usedPercentage
                ? `Context window ${usedPercentage} used`
                : `Context window ${formatContextWindowTokens(usage.usedTokens)} tokens used`
            }
          >
            <span className="relative flex h-3.5 w-3.5 items-center justify-center">
              <svg
                viewBox="0 0 16 16"
                className="-rotate-90 absolute inset-0 h-full w-full transform-gpu"
                aria-hidden="true"
              >
                <circle
                  cx="8"
                  cy="8"
                  r={radius}
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  opacity="0.2"
                />
                <circle
                  cx="8"
                  cy="8"
                  r={radius}
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeDasharray={circumference}
                  strokeDashoffset={dashOffset}
                  className="transition-[stroke-dashoffset] duration-500 ease-out motion-reduce:transition-none"
                />
              </svg>
            </span>
            <span className="tabular-nums font-medium leading-none">
              {usage.usedPercentage !== null
                ? `${Math.round(usage.usedPercentage)}%`
                : formatContextWindowTokens(usage.usedTokens)}
            </span>
          </button>
        }
      />
      <PopoverPopup tooltipStyle side="top" align="end" className="w-max max-w-none px-3 py-2">
        <div className="space-y-1.5 leading-tight">
          <div className="text-[11px] font-medium uppercase tracking-[0.08em] text-muted-foreground">
            Context window
          </div>
          {usage.maxTokens !== null && usedPercentage ? (
            <div className="whitespace-nowrap text-xs font-medium text-foreground">
              <span>{usedPercentage}</span>
              <span className="mx-1">⋅</span>
              <span>{formatContextWindowTokens(usage.usedTokens)}</span>
              <span>/</span>
              <span>{formatContextWindowTokens(usage.maxTokens ?? null)} context used</span>
            </div>
          ) : (
            <div className="text-sm text-foreground">
              {formatContextWindowTokens(usage.usedTokens)} tokens used so far
            </div>
          )}
          {(usage.totalProcessedTokens ?? null) !== null &&
          (usage.totalProcessedTokens ?? 0) > usage.usedTokens ? (
            <div className="text-xs text-muted-foreground">
              Total processed: {formatContextWindowTokens(usage.totalProcessedTokens ?? null)}{" "}
              tokens
            </div>
          ) : null}
          {usage.compactsAutomatically ? (
            <div className="text-xs text-muted-foreground">
              Automatically compacts its context when needed.
            </div>
          ) : null}
          {cumulativeCostUsd !== null && cumulativeCostUsd !== undefined ? (
            <div className="text-xs text-muted-foreground">
              Session cost: {formatCostUsd(cumulativeCostUsd)}
            </div>
          ) : null}
          <PlanLimits rows={planLimits ?? []} subscriptionType={planSubscriptionType} />
        </div>
      </PopoverPopup>
    </Popover>
  );
}
